import { useQuery } from '@apollo/client';
import { Navigate, useParams } from 'react-router-dom';

import { getFragment } from '../../../graphql/client/helpers/get-fragment';
import { GetPrescriberOrderDocument } from '../../../graphql/generated/graphql';
import { useHasEnhancedFlow } from '../../../hooks/use-has-enhanced-flow';
import { getAbsoluteRoute } from '../../../util/get-absolute-route';
import { Loader } from '../../common/Loader';

import OrderConfirmation from './OrderConfirmation';
import OrderConfirmationV2 from './OrderConfirmationV2';

const OrderConfirmationPage = () => {
    const { prescriberOrderId } = useParams();
    const { data, loading } = useQuery(GetPrescriberOrderDocument, {
        variables: { prescriberOrderId: prescriberOrderId ?? '' },
        skip: !prescriberOrderId,
        // The cached order may predate "Send to pharmacy" (no assigned
        // pharmacy yet); keep `loading` true until the fresh order arrives.
        fetchPolicy: 'cache-and-network',
    });

    const prescriberOrder = getFragment(data?.prescriberOrderV2);
    const hasEnhancedFlow = useHasEnhancedFlow(prescriberOrder);

    if (loading) {
        return <Loader />;
    }

    if (!prescriberOrderId) {
        return <Navigate to={getAbsoluteRoute('error')} />;
    }

    if (!prescriberOrder) {
        return <Navigate to={getAbsoluteRoute('notFound')} />;
    }

    if (prescriberOrder.__typename !== 'DigitallyReceivedPrescriberOrder') {
        return (
            <Navigate
                to={getAbsoluteRoute('order.prescriberOrderId.verifyPatient', {
                    prescriberOrderId,
                })}
            />
        );
    }

    if (hasEnhancedFlow) {
        return <OrderConfirmationV2 prescriberOrder={prescriberOrder} />;
    }

    return <OrderConfirmation prescriberOrder={prescriberOrder} />;
};

export default OrderConfirmationPage;
